import { Stack, Typography } from '@mui/material';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import { text } from '@/james-playground/aa-family-claim/claim/facts';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import { STAY } from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  gateQuestion,
  ChoiceCards,
  EntryField,
  EntryToggle,
  hasProblem,
  Note,
  Part,
  Parts,
  questionOf,
  Toggle,
  useDraft,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  stayLine,
  storedAs,
  str,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/**
 * A hospital, hospice or care home stay, now or in the last 6 weeks. The
 * form needs the kind of place but never asks it, so the card does. Then the
 * dates, the place, why (for a hospital) and, for a stay now, who pays. What
 * that means for payment is said before Continue, in words.
 */
const StayCard = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const { draft, set, visible, values } = useDraft(ask, facts);
  const qid = useQids();
  const gateId = ask.gate ?? '';
  const gate = str(draft[gateId]);
  const ids = ask.entries.filter((id) => id !== gateId && visible(id));
  const typeId = ids.find((id) => /PlaceType$/.test(id));
  const fundedId = ids.find((id) => id === 'publiclyFundedStay');
  const fundingId = ids.find((id) => id === 'fundingAuthority');
  const details = ids.filter(
    (id) => id !== typeId && id !== fundedId && id !== fundingId,
  );
  const placeType = str(draft[typeId ?? '']);
  const now = gateId === 'currentStay';
  const line =
    now && gate === 'Yes'
      ? stayLine(
          placeType,
          str(draft.publiclyFundedStay),
          text(facts, 'specialRules') === 'Yes',
        )
      : undefined;
  const stops =
    line === 'hospital' ||
    line === 'publicCareHome' ||
    line === 'publicHospice';

  const ready =
    gate === 'No' ||
    (gate === 'Yes' &&
      Boolean(placeType) &&
      (!fundedId || Boolean(str(draft[fundedId]))) &&
      !details.some((id) => hasProblem(id, draft[id])));

  const submit = () => {
    if (gate !== 'Yes') return onSubmit({ [gateId]: 'No' }, 'No');
    const out = values([gateId, ...ids]);
    for (const id of Object.keys(out))
      if (typeof out[id] === 'string')
        out[id] = storedAs(entry(id).answerType, out[id] as string);
    const name = str(out[now ? 'currentStayName' : 'previousStayName']);
    const from = str(out[now ? 'currentStayFrom' : 'previousStayFrom']);
    const to = str(out.previousStayTo);
    const when = now
      ? from
        ? `since ${from}`
        : ''
      : from && to
        ? `${from} to ${to}`
        : from
          ? `from ${from}`
          : '';
    onSubmit(
      out,
      [STAY.placeTypes[placeType] ?? placeType, name, when]
        .filter(Boolean)
        .join(', '),
    );
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={ready}
      onContinue={submit}
      onSkip={onSkip}
    >
      <Toggle
        {...gateQuestion(ask, v)}
        value={gate}
        onChange={(value) => set(gateId, value)}
      />
      {gate === 'Yes' && (
        <Parts>
          {typeId && (
            <Part qid={qid('type')} question={questionOf(typeId, v)}>
              <ChoiceCards
                labelledBy={qid('type')}
                options={(entry(typeId).options ?? []).map((o) => ({
                  value: o.label,
                  label: STAY.placeTypes[o.label] ?? o.label,
                }))}
                selected={placeType ? [placeType] : []}
                onPick={(value) => set(typeId, value)}
              />
            </Part>
          )}
          {placeType && details.length > 0 && (
            <Stack spacing={1.5} role='group' aria-labelledby={qid('details')}>
              <Typography id={qid('details')} variant='h6'>
                {STAY.details}
              </Typography>
              {details.map((id) => (
                <EntryField
                  key={id}
                  id={id}
                  v={v}
                  // The form gives the place's address a line each.
                  multiline={/(Address|Town)$/.test(id) ? false : undefined}
                  value={str(draft[id])}
                  onChange={(value) => set(id, value)}
                />
              ))}
            </Stack>
          )}
          {placeType && fundedId && (
            <Stack spacing={1.5}>
              <EntryToggle
                id={fundedId}
                v={v}
                value={str(draft[fundedId])}
                onChange={(value) => set(fundedId, value)}
              />
              {fundingId && (
                <EntryField
                  id={fundingId}
                  v={v}
                  value={str(draft[fundingId])}
                  onChange={(value) => set(fundingId, value)}
                />
              )}
            </Stack>
          )}
          {line && (
            <Note tone={stops ? 'warning' : 'info'}>
              <Typography variant='body1'>{STAY.title}</Typography>
              <Typography variant='body2'>{say(STAY.line[line], v)}</Typography>
              {stops && (
                <Typography variant='body2'>{say(STAY.carers, v)}</Typography>
              )}
            </Note>
          )}
        </Parts>
      )}
    </CardShell>
  );
};

StayCard.displayName = 'StayCard';
export default StayCard;
